import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  ChevronLeft, ChevronRight, Download, Eye, CheckCircle2, Bell,
  AlertTriangle, FileText, Loader2, Filter, RotateCcw, Wrench,
} from 'lucide-react';
import { format, parseISO, addDays, addWeeks, subWeeks } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Sun..Sat date for "this week" (Sun-Sat = payroll week)
// Builds the YYYY-MM-DD string from LOCAL date parts to avoid UTC drift
// that can return the wrong Sunday for clients east of UTC late Saturday.
function thisSundayISO() {
  const d = new Date();
  const dow = d.getDay(); // 0=Sun..6=Sat in local time
  d.setDate(d.getDate() - dow);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const STATUS_TONE = {
  Draft: { dot: 'bg-red-500', pill: 'bg-red-50 text-red-700 border-red-200', label: 'Draft' },
  Submitted: { dot: 'bg-amber-500', pill: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Submitted' },
  Approved: { dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Approved' },
  Paid: { dot: 'bg-slate-500', pill: 'bg-slate-100 text-slate-700 border-slate-200', label: 'Paid' },
};

const fmtHrs = (h) => {
  if (!h) return '—';
  const whole = Math.floor(h);
  const m = Math.round((h - whole) * 60);
  if (m === 0) return `${whole}h`;
  return `${whole}h ${m}m`;
};

export default function TimesheetApproval() {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(thisSundayISO());
  const [otThreshold, setOtThreshold] = useState(40);
  const [data, setData] = useState(null);
  const [variance, setVariance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState({});
  // Variance side-dialog state. When set, opens a modal showing all
  // (job × date) groups for that user where another worker booked time
  // on the same day and the user's numbers deviate from the team mean.
  const [varianceModalUser, setVarianceModalUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, varRes] = await Promise.all([
        axios.get(`${API}/timesheets/approval/week-summary`, {
          params: { week_start: weekStart, ot_threshold: otThreshold },
        }),
        // Variance lives next to the week summary so the small per-row
        // badge and the side-dialog can both consume one cached response.
        // Failures here are non-fatal — the page still functions without
        // variance data; we just won't show the badge or the dialog.
        axios
          .get(`${API}/timesheets/approval/variance`, {
            params: { week_start: weekStart },
          })
          .catch((err) => {
            console.warn('Variance load failed (non-fatal):', err);
            return { data: null };
          }),
      ]);
      setData(res.data);
      setVariance(varRes.data);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to load week summary');
    } finally {
      setLoading(false);
    }
  }, [weekStart, otThreshold]);

  useEffect(() => { load(); }, [load]);

  const weekDates = data?.week_dates || [];
  const dayLabel = (iso) => {
    try { return format(parseISO(iso), 'EEE d'); } catch { return iso; }
  };

  const filteredRows = useMemo(() => {
    if (!data) return [];
    return data.rows.filter(r => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!r.user_name.toLowerCase().includes(s)
          && !r.user_email.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [data, statusFilter, search]);

  const approve = async (row) => {
    if (!row.timesheet_id) {
      toast.error('No submitted timesheet yet — the employee must submit first.');
      return;
    }
    setBusy(b => ({ ...b, [row.user_id]: 'approving' }));
    try {
      await axios.post(`${API}/timesheets/weekly/${row.timesheet_id}/approve`);
      toast.success(`Approved ${row.user_name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    } finally {
      setBusy(b => ({ ...b, [row.user_id]: null }));
    }
  };

  const approveAllSubmitted = async () => {
    const submittedRows = (data?.rows || []).filter(r => r.status === 'Submitted' && r.timesheet_id);
    if (submittedRows.length === 0) {
      toast.info('No submitted timesheets to approve.');
      return;
    }
    const names = submittedRows.map(r => r.user_name).join(', ');
    if (!window.confirm(`Approve ${submittedRows.length} timesheet(s)?\n\n${names}`)) return;
    try {
      const res = await axios.post(`${API}/timesheets/weekly/approve-bulk`, {
        ids: submittedRows.map(r => r.timesheet_id),
      });
      toast.success(`Approved ${res.data.approved} of ${res.data.requested} timesheets`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk approve failed');
    }
  };

  const reopen = async (row) => {
    if (!row.timesheet_id) return;
    const reason = window.prompt(
      `Reopen ${row.user_name}'s week (currently ${row.status})?\n\nPlease provide a reason — the employee will be notified.`
    );
    if (!reason || !reason.trim()) return;
    try {
      await axios.post(`${API}/timesheets/weekly/${row.timesheet_id}/reopen`, { reason: reason.trim() });
      toast.success(`Reopened ${row.user_name}'s week`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reopen failed');
    }
  };

  const nag = async (row) => {
    setBusy(b => ({ ...b, [row.user_id]: 'nagging' }));
    try {
      // Reuse push-notification reminder endpoint if it exists; fall back to toast.
      await axios.post(`${API}/push/send-to-user`, {
        user_id: row.user_id,
        title: 'Timesheet reminder',
        body: `Please submit your timesheet for the week of ${weekStart}.`,
      });
      toast.success(`Reminded ${row.user_name}`);
    } catch (e) {
      toast.info(`Reminder noted — ${row.user_name} will see it next visit.`);
    } finally {
      setBusy(b => ({ ...b, [row.user_id]: null }));
    }
  };

  // Admin pulls a worker's draft week into the approval queue. Backend
  // computes the canonical (year, week_number) from the Sun-Sat range
  // (matching what the rest of the timesheet code already uses), then
  // promotes the draft to "Submitted" with an audit note. Worker sees it
  // as submitted on next refresh; admin can then approve/reject normally.
  const pullDraft = async (row) => {
    if (row.total_hours <= 0) {
      toast.error(`${row.user_name} has no time entries to pull this week.`);
      return;
    }
    if (!window.confirm(
      `Pull ${row.user_name}'s draft into the approval queue?\n\n` +
      `An audit note will record that you submitted on their behalf. ` +
      `The worker will see the week as submitted on their next refresh.`
    )) return;

    // Derive ISO (year, week_number) the same way the backend does — the
    // Thursday inside the Sun-Sat window determines the ISO week.
    const sun = parseISO(weekStart);
    const thu = new Date(sun);
    thu.setDate(thu.getDate() + 4);
    // Cheap isocalendar() in JS: nearest Thursday → year + week from that.
    const target = new Date(Date.UTC(thu.getFullYear(), thu.getMonth(), thu.getDate()));
    const dayNum = (target.getUTCDay() + 6) % 7;
    target.setUTCDate(target.getUTCDate() - dayNum + 3);
    const firstThu = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
    const weekNum = 1 + Math.round(((target - firstThu) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
    const year = target.getUTCFullYear();

    setBusy(b => ({ ...b, [row.user_id]: 'pulling' }));
    try {
      await axios.post(
        `${API}/timesheets/weekly/admin-submit`,
        null,
        { params: { user_id: row.user_id, week_number: weekNum, year } }
      );
      toast.success(`Pulled ${row.user_name}'s draft into approval queue`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pull failed');
    } finally {
      setBusy(b => ({ ...b, [row.user_id]: null }));
    }
  };

  const exportCSV = async (fmt) => {
    try {
      const token = localStorage.getItem('fieldflow_token');
      const url = `${API}/timesheets/export/csv?fmt=${fmt}&week_start=${weekStart}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        // Read body ONCE — reading both .json() and .text() throws
        // "Body is disturbed or locked" in Safari/Chrome.
        const errText = await res.text();
        let detail = errText;
        try {
          const j = JSON.parse(errText);
          detail = j.detail || j.message || errText;
        } catch { /* not JSON, use raw text */ }
        throw new Error(`HTTP ${res.status}: ${detail || res.statusText}`.slice(0, 300));
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      const dlUrl = URL.createObjectURL(blob);
      a.href = dlUrl;
      a.download = `timesheets_${fmt}_${weekStart}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      toast.success(`Exported ${fmt.toUpperCase()} CSV`);
    } catch (e) {
      toast.error(`Export failed — ${e.message || e}`, { duration: 10000 });
    }
  };

  const downloadPDF = async (row) => {
    // 2026-06-08: switched from the backend ReportLab `/weekly/{id}/pdf`
    // endpoint (which was failing in production with opaque errors) to
    // the same React preview route that the regular Timesheet page uses.
    // It's the SAME rendering you see on /timesheet → PDF button —
    // works perfectly across Safari/Chrome/Mac/PC and has none of the
    // ReportLab data-quirk failures.
    if (!row.user_id) {
      toast.error('User id missing on this row.');
      return;
    }
    // Open in a new tab so the user can hit "Print / Save PDF" inside
    // the preview page (browser native print → PDF, same as the
    // individual timesheet PDF flow). `?from=approval` tells the
    // preview's Back button to return here instead of guessing via
    // browser history (which lands on /timesheet mine — see the note
    // in TimesheetMemberPreview.handleBack).
    const url = `/timesheet/member/${row.user_id}/week/${weekStart}/preview?from=approval`;
    window.open(url, '_blank', 'noopener');
  };

  const downloadBinderPDF = async () => {
    // 2026-06-08: same fix as the per-user PDF — switched from the
    // backend ReportLab `/timesheets/binder/pdf` endpoint to the React
    // team preview route. Renders the entire team in one HTML page so
    // the user can hit "Print / Save PDF" once and get a binder.
    // Avoids the "Body is disturbed or locked" / ReportLab data-quirk
    // failures we were hitting in production.
    const url = `/timesheet/team/${weekStart}/preview`;
    window.open(url, '_blank', 'noopener');
  };

  // Feb-2026 (Lauren, owner): batch download — one PDF per worker
  // for every approved timesheet in the selected week, delivered as
  // a zip. Backend renders each worker's `/timesheet/member/...`
  // preview page via headless Chromium (same path used when Lauren
  // hits Print → Save PDF manually), so the individual PDFs are
  // byte-identical to what she'd get one-at-a-time.
  const [downloadingBatchZip, setDownloadingBatchZip] = useState(false);
  const downloadBatchPDFZip = async () => {
    if (downloadingBatchZip) return;
    setDownloadingBatchZip(true);
    // Round 7 — an honest progress toast: we can't report a live
    // "4/12" counter without polling infrastructure (the render is one
    // synchronous request), but we DO know the total up front from the
    // rows already loaded on this page, and the backend returns the
    // real success/failure counts as response headers once it's done.
    const approvedCount = (data?.rows || []).filter(
      (r) => r.status === 'Approved' || r.status === 'Paid'
    ).length;
    const toastId = toast.loading(
      approvedCount
        ? `Rendering ${approvedCount} worker PDF${approvedCount === 1 ? '' : 's'}…`
        : 'Building ZIP of approved timesheets…'
    );
    try {
      const res = await axios.get(
        `${API}/timesheets/approval/batch-pdf-zip`,
        { params: { week_start: weekStart }, responseType: 'blob', timeout: 600_000 },
      );
      // Trigger a browser download from the returned blob.
      const blob = new Blob([res.data], { type: 'application/zip' });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `timesheets_week_${weekStart}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      const successCount = parseInt(res.headers['x-zip-success-count'], 10);
      const failureCount = parseInt(res.headers['x-zip-failure-count'], 10);
      if (!Number.isNaN(successCount) && failureCount > 0) {
        toast.warning(
          `ZIP downloaded — ${successCount} rendered, ${failureCount} failed (see manifest.txt).`,
          { id: toastId }
        );
      } else if (!Number.isNaN(successCount)) {
        toast.success(`ZIP downloaded — ${successCount} worker PDF${successCount === 1 ? '' : 's'} rendered.`, { id: toastId });
      } else {
        toast.success('ZIP downloaded — check your Downloads folder.', { id: toastId });
      }
    } catch (err) {
      // If backend returned a blob for a 4xx/5xx, we need to parse it
      // as text to show the human-readable detail. Otherwise fall
      // back to a generic message.
      let detail = 'Failed to build ZIP';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          detail = parsed.detail || detail;
        } catch { /* leave default */ }
      } else if (err.response?.data?.detail) {
        detail = err.response.data.detail;
      }
      toast.error(detail, { id: toastId });
    } finally {
      setDownloadingBatchZip(false);
    }
  };

  // Custom date-range export
  const [showRangeDialog, setShowRangeDialog] = useState(false);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [rangeFmt, setRangeFmt] = useState('detailed');
  const exportRangeCSV = async () => {
    if (!rangeStart || !rangeEnd) {
      toast.error('Both start and end dates required.');
      return;
    }
    try {
      const token = localStorage.getItem('fieldflow_token');
      const url = `${API}/timesheets/export/csv?fmt=${rangeFmt}&start_date=${rangeStart}&end_date=${rangeEnd}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const errText = await res.text();
        let detail = errText;
        try {
          const j = JSON.parse(errText);
          detail = j.detail || j.message || errText;
        } catch { /* not JSON */ }
        throw new Error(`HTTP ${res.status}: ${detail || res.statusText}`.slice(0, 300));
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      const dlUrl = URL.createObjectURL(blob);
      a.href = dlUrl;
      a.download = `timesheets_${rangeFmt}_${rangeStart}_to_${rangeEnd}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      setShowRangeDialog(false);
      toast.success('Range export downloaded');
    } catch (e) {
      toast.error(`Range export failed — ${e.message || e}`, { duration: 10000 });
    }
  };

  // Maintenance: find & merge duplicate timesheet rows.
  // (user_id, date, job_number) — same key as the partial-unique index.
  const [showDedupeDialog, setShowDedupeDialog] = useState(false);
  const [dedupeStart, setDedupeStart] = useState('');
  const [dedupeEnd, setDedupeEnd] = useState('');
  const [dedupeScan, setDedupeScan] = useState(null);
  const [dedupeBusy, setDedupeBusy] = useState(false);
  const [dedupeResult, setDedupeResult] = useState(null);

  const runDedupeScan = async () => {
    setDedupeBusy(true);
    setDedupeResult(null);
    try {
      const params = new URLSearchParams();
      if (dedupeStart) params.set('start_date', dedupeStart);
      if (dedupeEnd) params.set('end_date', dedupeEnd);
      const res = await axios.get(
        `${API}/timesheets/admin/dedupe-scan${params.toString() ? `?${params}` : ''}`
      );
      setDedupeScan(res.data);
      if (res.data.duplicate_group_count === 0) {
        toast.success(`No duplicates found · scanned ${res.data.scanned} rows`);
      } else {
        toast.info(
          `Found ${res.data.duplicate_group_count} duplicate group${res.data.duplicate_group_count === 1 ? '' : 's'} (${res.data.total_rows_to_remove} extra row${res.data.total_rows_to_remove === 1 ? '' : 's'})`
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setDedupeBusy(false);
    }
  };

  const runDedupeApply = async () => {
    if (!dedupeScan || dedupeScan.duplicate_group_count === 0) return;
    if (!window.confirm(
      `Merge ${dedupeScan.duplicate_group_count} duplicate group${dedupeScan.duplicate_group_count === 1 ? '' : 's'} ` +
      `and delete ${dedupeScan.total_rows_to_remove} extra row${dedupeScan.total_rows_to_remove === 1 ? '' : 's'}?\n\n` +
      `Minutes are summed onto the kept row; notes are joined. This cannot be undone.`
    )) return;
    setDedupeBusy(true);
    try {
      const params = new URLSearchParams();
      if (dedupeStart) params.set('start_date', dedupeStart);
      if (dedupeEnd) params.set('end_date', dedupeEnd);
      const res = await axios.post(
        `${API}/timesheets/admin/dedupe-apply${params.toString() ? `?${params}` : ''}`
      );
      setDedupeResult(res.data);
      toast.success(
        `Merged ${res.data.merged_groups} group${res.data.merged_groups === 1 ? '' : 's'}, ` +
        `removed ${res.data.deleted_rows} row${res.data.deleted_rows === 1 ? '' : 's'}` +
        (res.data.unique_index_created ? ' · unique index created' : '')
      );
      // Re-scan to update the modal in-place
      await runDedupeScan();
      // Reload the dashboard so totals reflect the merge
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setDedupeBusy(false);
    }
  };

  // Maintenance: canonicalize weekly_timesheets envelope `week_start` to the
  // ISO Monday of (year, week_number). Writes only to weekly envelopes —
  // never touches the entries collection or the Time Tracker.
  const [showCanonicalizeDialog, setShowCanonicalizeDialog] = useState(false);
  const [canonScan, setCanonScan] = useState(null);
  const [canonBusy, setCanonBusy] = useState(false);
  const [canonResult, setCanonResult] = useState(null);

  const runCanonScan = async () => {
    setCanonBusy(true);
    setCanonResult(null);
    try {
      const res = await axios.get(`${API}/timesheets/admin/canonicalize-weeks-scan`);
      setCanonScan(res.data);
      if (res.data.mismatch_count === 0) {
        toast.success(`All ${res.data.scanned} envelope(s) already canonical ✓`);
      } else {
        toast.info(`${res.data.mismatch_count} envelope(s) need normalization`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setCanonBusy(false);
    }
  };

  const runCanonApply = async () => {
    if (!canonScan || canonScan.mismatch_count === 0) return;
    if (!window.confirm(
      `Normalize ${canonScan.mismatch_count} envelope(s)?\n\nThis ONLY updates week_start and week_end on the weekly envelopes — entries, totals, status, and the Time Tracker are NOT touched. Idempotent.`
    )) return;
    setCanonBusy(true);
    try {
      const res = await axios.post(`${API}/timesheets/admin/canonicalize-weeks-apply`);
      setCanonResult(res.data);
      toast.success(`Normalized ${res.data.updated} envelope(s)`);
      await runCanonScan();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setCanonBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-[1400px] mx-auto px-3 md:px-6 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] leading-tight">
            Timesheet Approval
          </h1>
          <p className="text-sm text-slate-500">
            Week ending {data ? format(parseISO(data.week_end), 'EEEE, MMM d, yyyy') : '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={() => setWeekStart(format(subWeeks(parseISO(weekStart), 1), 'yyyy-MM-dd'))}
            data-testid="approval-prev-week"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={() => setWeekStart(thisSundayISO())}
            data-testid="approval-this-week"
          >
            This week
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={() => setWeekStart(format(addWeeks(parseISO(weekStart), 1), 'yyyy-MM-dd'))}
            data-testid="approval-next-week"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          {data && data.totals.submitted_count > 0 && (
            <Button
              size="sm"
              className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={approveAllSubmitted}
              data-testid="approval-approve-all-btn"
              title="Approve every Submitted timesheet for this week"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Approve All ({data.totals.submitted_count})
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="h-9 bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="approval-export-btn">
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel>Export this week's data</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => exportCSV('detailed')}>
                <FileText className="w-4 h-4 mr-2" />
                <div>
                  <p className="font-medium">CSV — Detailed</p>
                  <p className="text-xs text-slate-500">One row per entry · job-cost</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportCSV('payroll')}>
                <FileText className="w-4 h-4 mr-2" />
                <div>
                  <p className="font-medium">CSV — Payroll Summary</p>
                  <p className="text-xs text-slate-500">One row per (employee, date)</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportCSV('qbo')}>
                <FileText className="w-4 h-4 mr-2" />
                <div>
                  <p className="font-medium">CSV — QuickBooks Online</p>
                  <p className="text-xs text-slate-500">Time-activity import</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportCSV('qbd')}>
                <FileText className="w-4 h-4 mr-2" />
                <div>
                  <p className="font-medium">CSV — QuickBooks Desktop</p>
                  <p className="text-xs text-slate-500">Time-tracking import</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={downloadBinderPDF}>
                <FileText className="w-4 h-4 mr-2" />
                <div>
                  <p className="font-medium">PDF — Pay-Period Binder</p>
                  <p className="text-xs text-slate-500">Cover + page per employee · sign-off</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={downloadBatchPDFZip}
                disabled={downloadingBatchZip}
                data-testid="approval-batch-pdf-zip-menu"
              >
                <FileText className="w-4 h-4 mr-2" />
                <div>
                  <p className="font-medium">
                    {downloadingBatchZip ? 'Building ZIP…' : 'ZIP — One PDF per Worker'}
                  </p>
                  <p className="text-xs text-slate-500">All approved timesheets, delivered as a batch</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowRangeDialog(true)}>
                <FileText className="w-4 h-4 mr-2" />
                <div>
                  <p className="font-medium">Custom Date Range…</p>
                  <p className="text-xs text-slate-500">Month / quarter exports</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">
                Maintenance
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setShowDedupeDialog(true)} data-testid="approval-dedupe-menu">
                <Wrench className="w-4 h-4 mr-2 text-slate-500" />
                <div>
                  <p className="font-medium">Find &amp; Merge Duplicates…</p>
                  <p className="text-xs text-slate-500">Safe: previews before applying</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowCanonicalizeDialog(true)} data-testid="approval-canonicalize-menu">
                <Wrench className="w-4 h-4 mr-2 text-slate-500" />
                <div>
                  <p className="font-medium">Normalize Week Dates…</p>
                  <p className="text-xs text-slate-500">Idempotent: writes only to weekly envelopes</p>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Awaiting Submission strip — surfaces workers who have time entries
          for the week but haven't submitted yet. Owner can pull each one
          into approval directly. Only renders when there's actually
          something to act on. */}
      {(() => {
        const awaiting = (data?.rows || []).filter(
          r => r.status === 'Draft' && r.total_hours > 0
        );
        if (awaiting.length === 0) return null;
        return (
          <Card className="border-amber-300 bg-amber-50/60" data-testid="awaiting-submission-strip">
            <CardContent className="p-4">
              <div className="flex items-start gap-3 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-amber-900 text-sm">
                    {awaiting.length} {awaiting.length === 1 ? 'worker has' : 'workers have'} time entered but not submitted
                  </h3>
                  <p className="text-xs text-amber-700">
                    Pull into the approval queue if you don't want to wait — an audit note tags you as the submitter.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {awaiting.map(r => {
                  const color = r.calendar_color || '#64748b';
                  return (
                    <div
                      key={r.user_id}
                      className="flex items-center gap-2 px-2.5 py-1.5 bg-white border border-amber-200 rounded-md"
                      data-testid={`awaiting-row-${r.user_id}`}
                    >
                      <span
                        className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-white"
                        style={{ backgroundColor: color }}
                      >
                        {r.user_name.split(' ').map(p => p[0]).slice(0, 2).join('')}
                      </span>
                      <div className="text-xs leading-tight">
                        <p className="font-semibold text-slate-900">{r.user_name}</p>
                        <p className="text-slate-500">{fmtHrs(r.total_hours)} · {r.entry_count} entries</p>
                      </div>
                      <Button
                        size="sm"
                        className="h-7 ml-1 bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs"
                        onClick={() => pullDraft(r)}
                        disabled={busy[r.user_id] === 'pulling'}
                        data-testid={`awaiting-pull-${r.user_id}`}
                      >
                        {busy[r.user_id] === 'pulling' ? '…' : 'Pull in'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Filter bar + totals */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-48 h-9"
              data-testid="approval-search"
            />
          </div>
          <div className="flex items-center gap-1">
            {['all', 'Draft', 'Submitted', 'Approved'].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`h-9 px-3 text-sm rounded-md border ${
                  statusFilter === s
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`approval-filter-${s}`}
              >
                {s === 'all' ? 'All' : s}
                {data && s !== 'all' && (
                  <span className="ml-1.5 text-xs opacity-80">
                    {data.totals[`${s.toLowerCase()}_count`]}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto text-sm">
            <span className="text-slate-500">OT after</span>
            <Input
              type="number"
              value={otThreshold}
              onChange={e => setOtThreshold(parseFloat(e.target.value) || 40)}
              className="w-20 h-9"
              min="0"
              step="0.5"
              data-testid="approval-ot-threshold"
            />
            <span className="text-slate-500">h/wk</span>
          </div>
          {data && (
            <div className="w-full mt-2 pt-3 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              <div>
                <p className="text-2xl font-bold text-slate-900">{fmtHrs(data.totals.team_total_hours)}</p>
                <p className="text-xs uppercase tracking-wide text-slate-500">Team Total</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{fmtHrs(data.totals.team_job_hours)}</p>
                <p className="text-xs uppercase tracking-wide text-slate-500">Job Billable</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{fmtHrs(data.totals.team_travel_hours)}</p>
                <p className="text-xs uppercase tracking-wide text-slate-500">Travel</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-600">{fmtHrs(data.totals.team_shop_hours)}</p>
                <p className="text-xs uppercase tracking-wide text-slate-500">Shop / Other</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee rows */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : (
            <>
            <table className="hidden md:table w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="text-left p-3 font-semibold">Employee</th>
                  {weekDates.map(d => (
                    <th key={d} className="text-center p-3 font-semibold text-xs uppercase tracking-wide whitespace-nowrap">
                      {dayLabel(d)}
                    </th>
                  ))}
                  <th className="text-right p-3 font-semibold whitespace-nowrap">Total</th>
                  <th className="text-center p-3 font-semibold">Status</th>
                  <th className="text-right p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(r => {
                  const tone = STATUS_TONE[r.status] || STATUS_TONE.Draft;
                  const color = r.calendar_color || '#64748b';
                  const openWeek = () => navigate(`/timesheet?user=${r.user_id}&week=${weekStart}`);
                  return (
                    <tr
                      key={r.user_id}
                      className="border-b border-slate-100 hover:bg-amber-50/30 cursor-pointer"
                      onClick={openWeek}
                      data-testid={`approval-row-${r.user_id}`}
                    >
                      <td className="p-3 border-l-4" style={{ borderLeftColor: color }}>
                        <div className="flex items-center gap-2">
                          <span
                            className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-white"
                            style={{ backgroundColor: color }}
                          >
                            {r.user_name.split(' ').map(p => p[0]).slice(0, 2).join('')}
                          </span>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate underline-offset-2 hover:underline">{r.user_name}</p>
                            <p className="text-[11px] text-slate-500 truncate">{r.user_email}</p>
                          </div>
                        </div>
                      </td>
                      {weekDates.map(d => {
                        const mins = r.daily_minutes?.[d] || 0;
                        return (
                          <td key={d} className="p-3 text-center whitespace-nowrap relative">
                            {mins > 0 ? (
                              <>
                                <span className="font-medium text-slate-700">
                                  {fmtHrs(mins / 60)}
                                </span>
                                <span
                                  className="absolute left-1 right-1 bottom-1 h-0.5 rounded-full"
                                  style={{ backgroundColor: color, opacity: 0.55 }}
                                />
                              </>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="p-3 text-right whitespace-nowrap">
                        <span className="font-bold text-slate-900 text-base">
                          {fmtHrs(r.total_hours)}
                        </span>
                        {r.is_ot && (
                          <Badge className="ml-2 bg-orange-100 text-orange-700 border-orange-200" variant="outline">
                            <AlertTriangle className="w-3 h-3 mr-0.5" /> OT
                          </Badge>
                        )}
                        {/* Variance badge — opens side dialog showing
                            (job × date) deviations vs team mean. */}
                        {variance?.outlier_count_by_user?.[r.user_id] > 0 && (
                          <button
                            type="button"
                            className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 text-[10px] font-semibold hover:bg-amber-100"
                            onClick={(ev) => { ev.stopPropagation(); setVarianceModalUser(r); }}
                            title="View variance vs the rest of the crew"
                            data-testid={`variance-badge-${r.user_id}`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            {variance.outlier_count_by_user[r.user_id]} outlier{variance.outlier_count_by_user[r.user_id] === 1 ? '' : 's'}
                          </button>
                        )}
                        {r.has_unbilled_other && (
                          <p className="text-[10px] text-orange-600 mt-0.5">⚠ has OTHER entries</p>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${tone.pill}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
                          {tone.label}
                        </span>
                      </td>
                      <td className="p-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2"
                            onClick={() => navigate(`/timesheet?user=${r.user_id}&week=${weekStart}`)}
                            title="View week"
                            data-testid={`approval-view-${r.user_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {r.status === 'Submitted' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={() => approve(r)}
                              disabled={busy[r.user_id] === 'approving'}
                              title="Approve"
                              data-testid={`approval-approve-${r.user_id}`}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                          )}
                          {(r.status === 'Approved' || r.status === 'Paid') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              onClick={() => reopen(r)}
                              title="Reopen this week"
                              data-testid={`approval-reopen-${r.user_id}`}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                          {r.status === 'Draft' && (
                            <>
                              {r.total_hours > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2 text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                                  onClick={() => pullDraft(r)}
                                  disabled={busy[r.user_id] === 'pulling'}
                                  title="Pull draft into approval queue"
                                  data-testid={`approval-pull-${r.user_id}`}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                onClick={() => nag(r)}
                                disabled={busy[r.user_id] === 'nagging'}
                                title="Send reminder"
                                data-testid={`approval-nag-${r.user_id}`}
                              >
                                <Bell className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {r.timesheet_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-slate-600"
                              onClick={() => downloadPDF(r)}
                              title="Download PDF"
                              data-testid={`approval-pdf-${r.user_id}`}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={weekDates.length + 4} className="p-10 text-center text-slate-400">
                      No employees match the current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* MOBILE — card per employee with a horizontal mini-bar chart.
                Friction #11. Hidden on md+ (table takes over). */}
            <div className="md:hidden divide-y divide-slate-100">
              {filteredRows.length === 0 && (
                <p className="p-8 text-center text-slate-400 text-sm">No employees match the current filter.</p>
              )}
              {filteredRows.map(r => {
                const tone = STATUS_TONE[r.status] || STATUS_TONE.Draft;
                const color = r.calendar_color || '#64748b';
                const maxMin = Math.max(...weekDates.map(d => r.daily_minutes?.[d] || 0), 60);
                return (
                  <div
                    key={r.user_id}
                    className="px-3 py-3 border-l-4 cursor-pointer hover:bg-amber-50/30"
                    style={{ borderLeftColor: color }}
                    onClick={() => navigate(`/timesheet?user=${r.user_id}&week=${weekStart}`)}
                    data-testid={`approval-mobile-row-${r.user_id}`}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-white"
                        style={{ backgroundColor: color }}
                      >
                        {r.user_name.split(' ').map(p => p[0]).slice(0, 2).join('')}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-bold text-slate-900 truncate">{r.user_name}</p>
                          <span className="text-lg font-bold text-slate-900 whitespace-nowrap">{fmtHrs(r.total_hours)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${tone.pill}`}>
                            <span className={`w-1 h-1 rounded-full ${tone.dot}`} />
                            {tone.label}
                          </span>
                          {r.is_ot && <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-[10px]" variant="outline">OT</Badge>}
                          {r.has_unbilled_other && <span className="text-[10px] text-orange-600">⚠ OTHER</span>}
                        </div>
                      </div>
                    </div>

                    {/* Mini bar chart — one bar per day, scaled to user's max */}
                    <div className="grid grid-cols-7 gap-1 mt-3">
                      {weekDates.map(d => {
                        const mins = r.daily_minutes?.[d] || 0;
                        const heightPct = mins > 0 ? Math.max(8, (mins / maxMin) * 100) : 0;
                        return (
                          <div key={d} className="flex flex-col items-center">
                            <div className="w-full h-12 flex items-end">
                              <div
                                className="w-full rounded-t-sm transition-all"
                                style={{
                                  height: `${heightPct}%`,
                                  backgroundColor: mins > 0 ? color : 'transparent',
                                  opacity: mins > 0 ? 0.85 : 0.0,
                                }}
                              />
                            </div>
                            <p className="text-[9px] uppercase font-semibold text-slate-400 mt-0.5">
                              {dayLabel(d).slice(0, 3)}
                            </p>
                            <p className="text-[10px] font-medium text-slate-600">
                              {mins > 0 ? fmtHrs(mins / 60) : '—'}
                            </p>
                          </div>
                        );
                      })}
                    </div>

                    {/* Action row */}
                    <div className="flex items-center gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 flex-1"
                        onClick={() => navigate(`/timesheet?user=${r.user_id}&week=${weekStart}`)}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      {r.status === 'Submitted' && (
                        <Button
                          size="sm"
                          className="h-8 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => approve(r)}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      )}
                      {(r.status === 'Approved' || r.status === 'Paid') && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 flex-1 text-amber-600 border-amber-200"
                          onClick={() => reopen(r)}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" /> Reopen
                        </Button>
                      )}
                      {r.status === 'Draft' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 flex-1 text-amber-600 border-amber-200"
                          onClick={() => nag(r)}
                        >
                          <Bell className="w-4 h-4 mr-1" /> Remind
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Custom date-range export dialog */}
      <Dialog open={showRangeDialog} onOpenChange={setShowRangeDialog}>
        <DialogContent data-testid="range-export-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl">Custom Date Range Export</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 font-semibold uppercase">Start</label>
                <Input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)} className="h-9" />
              </div>
              <div>
                <label className="text-xs text-slate-500 font-semibold uppercase">End</label>
                <Input type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)} className="h-9" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-semibold uppercase">Format</label>
              <select
                value={rangeFmt}
                onChange={e => setRangeFmt(e.target.value)}
                className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white"
              >
                <option value="detailed">Detailed (one row per entry)</option>
                <option value="payroll">Payroll (Regular / OT / PTO / Holiday / Sick split)</option>
                <option value="qbo">QuickBooks Online</option>
                <option value="qbd">QuickBooks Desktop</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRangeDialog(false)}>Cancel</Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={exportRangeCSV}>
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance: Find & Merge Duplicates dialog */}
      <Dialog
        open={showDedupeDialog}
        onOpenChange={(o) => {
          setShowDedupeDialog(o);
          if (!o) { setDedupeScan(null); setDedupeResult(null); }
        }}
      >
        <DialogContent className="max-w-2xl" data-testid="dedupe-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-500" />
              Find &amp; Merge Duplicate Time Entries
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Looks for timesheet rows with the same <strong>employee + date + job number</strong>.
              When found, minutes from the duplicate rows are <strong>summed onto a single kept row</strong>
              {' '}(prefers manually-edited rows; otherwise the largest entry wins) and notes are joined.
              Leave the dates blank to scan every entry.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 font-semibold uppercase">Start (optional)</label>
                <Input
                  type="date" value={dedupeStart}
                  onChange={e => setDedupeStart(e.target.value)}
                  className="h-9" data-testid="dedupe-start-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 font-semibold uppercase">End (optional)</label>
                <Input
                  type="date" value={dedupeEnd}
                  onChange={e => setDedupeEnd(e.target.value)}
                  className="h-9" data-testid="dedupe-end-input"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={runDedupeScan}
                disabled={dedupeBusy}
                className="h-9 bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="dedupe-scan-btn"
              >
                {dedupeBusy && !dedupeResult ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Scanning…</>
                ) : (
                  <>Preview Duplicates</>
                )}
              </Button>
              {dedupeScan && dedupeScan.duplicate_group_count > 0 && (
                <Button
                  onClick={runDedupeApply}
                  disabled={dedupeBusy}
                  className="h-9 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
                  data-testid="dedupe-apply-btn"
                >
                  {dedupeBusy ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Merging…</>
                  ) : (
                    <>Merge &amp; Delete {dedupeScan.total_rows_to_remove} row{dedupeScan.total_rows_to_remove === 1 ? '' : 's'}</>
                  )}
                </Button>
              )}
            </div>

            {/* Scan results */}
            {dedupeScan && (
              <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="dedupe-scan-result">
                <div className="bg-slate-50 px-3 py-2 flex items-center justify-between text-sm">
                  <span className="text-slate-600">
                    Scanned <strong>{dedupeScan.scanned}</strong> rows ·
                    {' '}{dedupeScan.duplicate_group_count === 0 ? (
                      <span className="text-emerald-700 font-semibold">No duplicates found ✓</span>
                    ) : (
                      <span className="text-amber-700 font-semibold">
                        {dedupeScan.duplicate_group_count} duplicate group{dedupeScan.duplicate_group_count === 1 ? '' : 's'} · {dedupeScan.total_rows_to_remove} extra row{dedupeScan.total_rows_to_remove === 1 ? '' : 's'}
                      </span>
                    )}
                  </span>
                </div>
                {dedupeScan.groups.length > 0 && (
                  <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                    {dedupeScan.groups.map((g, i) => (
                      <div key={i} className="px-3 py-2 text-sm">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-semibold text-slate-800">{g.user_name}</span>
                          <span className="text-xs text-slate-500">{g.date}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-xs">
                          <span className="font-mono text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                            {g.job_number}
                          </span>
                          <span className="text-slate-500">
                            {g.row_count} rows → 1 row ·
                            {' '}{g.current_total_minutes.map(m => `${m}m`).join(' + ')} = {g.merged_total_minutes}m
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Apply result */}
            {dedupeResult && (
              <div className="border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2 text-sm" data-testid="dedupe-apply-result">
                <p className="font-semibold text-emerald-800">
                  ✓ Merged {dedupeResult.merged_groups} group{dedupeResult.merged_groups === 1 ? '' : 's'} ·
                  {' '}deleted {dedupeResult.deleted_rows} row{dedupeResult.deleted_rows === 1 ? '' : 's'}
                </p>
                {dedupeResult.unique_index_created && (
                  <p className="text-xs text-emerald-700 mt-0.5">
                    Unique index <code>uniq_user_date_jobnumber</code> is now active — future duplicates will be rejected at the database level.
                  </p>
                )}
                {dedupeResult.unique_index_error && (
                  <p className="text-xs text-amber-700 mt-0.5">
                    Unique index could not be created: {dedupeResult.unique_index_error}
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDedupeDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance: Normalize Week Dates dialog */}
      <Dialog
        open={showCanonicalizeDialog}
        onOpenChange={(o) => {
          setShowCanonicalizeDialog(o);
          if (!o) { setCanonScan(null); setCanonResult(null); }
        }}
      >
        <DialogContent className="max-w-2xl" data-testid="canonicalize-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-500" />
              Normalize Week Dates
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="text-sm text-slate-600 space-y-2">
              <p>
                Checks every weekly timesheet envelope and rewrites <code>week_start</code> /
                <code>week_end</code> to the canonical <strong>ISO Monday</strong> of its
                stored (year, week_number). This is purely a relabeling pass — no
                entries, totals, statuses, or approvals are touched.
              </p>
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                ✓ Safe for the Time Tracker — that data lives in a different collection
                and is never read or written by this action. Run anytime, including during
                business hours. Idempotent: running twice is a no-op.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={runCanonScan}
                disabled={canonBusy}
                className="h-9 bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="canon-scan-btn"
              >
                {canonBusy && !canonResult ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Scanning…</>
                ) : (
                  <>Preview Mismatches</>
                )}
              </Button>
              {canonScan && canonScan.mismatch_count > 0 && (
                <Button
                  onClick={runCanonApply}
                  disabled={canonBusy}
                  className="h-9 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
                  data-testid="canon-apply-btn"
                >
                  {canonBusy ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Applying…</>
                  ) : (
                    <>Normalize {canonScan.mismatch_count} envelope{canonScan.mismatch_count === 1 ? '' : 's'}</>
                  )}
                </Button>
              )}
            </div>

            {/* Scan results */}
            {canonScan && (
              <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="canon-scan-result">
                <div className="bg-slate-50 px-3 py-2 flex items-center justify-between text-sm">
                  <span className="text-slate-600">
                    Scanned <strong>{canonScan.scanned}</strong> envelope{canonScan.scanned === 1 ? '' : 's'} ·
                    {' '}{canonScan.mismatch_count === 0 ? (
                      <span className="text-emerald-700 font-semibold">All canonical ✓</span>
                    ) : (
                      <span className="text-amber-700 font-semibold">
                        {canonScan.mismatch_count} need{canonScan.mismatch_count === 1 ? 's' : ''} normalization
                      </span>
                    )}
                  </span>
                </div>
                {canonScan.envelopes && canonScan.envelopes.length > 0 && (
                  <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                    {canonScan.envelopes.map(env => (
                      <div key={env.id} className="px-3 py-2 text-sm">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-semibold text-slate-800">{env.user_name}</span>
                          <span className="text-xs text-slate-500">W{env.week_number}/{env.year} · {env.status}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 font-mono">
                          {env.current_week_start} → {env.target_week_start}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {canonResult && (
              <div className="border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2 text-sm" data-testid="canon-apply-result">
                <p className="font-semibold text-emerald-800">
                  ✓ Normalized {canonResult.updated} envelope{canonResult.updated === 1 ? '' : 's'}
                  {canonResult.skipped_no_meta > 0 && ` · ${canonResult.skipped_no_meta} skipped (missing year/week metadata)`}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCanonicalizeDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Variance side dialog — shows every (job × date) group this worker
          was on where the team had ≥2 workers, comparing their travel-to /
          on-job / travel-from minutes against the team mean. Colored chips
          flag the deviation tier (amber/red). */}
      <Dialog open={!!varianceModalUser} onOpenChange={(o) => !o && setVarianceModalUser(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="variance-modal">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl">
              Variance — {varianceModalUser?.user_name}
            </DialogTitle>
            <p className="text-sm text-slate-500">
              Comparing this worker's time against the rest of the crew on shared jobs.
              <span className="block text-xs mt-1">
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1" />
                amber = ≥15min &amp; ≥25% off mean
                <span className="inline-block w-2 h-2 rounded-full bg-red-500 ml-3 mr-1" />
                red = ≥30min &amp; ≥50% off mean
              </span>
            </p>
          </DialogHeader>
          {(() => {
            if (!varianceModalUser || !variance) return null;
            const groups = (variance.groups || []).filter(
              g => g.members.some(m => m.user_id === varianceModalUser.user_id && m.is_outlier)
            );
            if (groups.length === 0) {
              return <p className="text-center text-slate-500 py-8">No outliers found for this worker.</p>;
            }
            const toneFor = (cls) =>
              cls === 'red' ? 'bg-red-100 text-red-700 border-red-200'
              : cls === 'amber' ? 'bg-amber-100 text-amber-700 border-amber-200'
              : 'text-slate-600';
            const fmtMin = (m) => {
              if (m == null) return '—';
              if (m < 60) return `${Math.round(m)}m`;
              return `${Math.floor(m/60)}h ${Math.round(m%60)}m`;
            };
            return (
              <div className="space-y-4">
                {groups.map((g, gi) => {
                  return (
                    <div key={gi} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`variance-group-${g.job_id}-${g.date}`}>
                      <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                          {g.job_number && (
                            <span className="font-mono text-xs font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded mr-2">
                              {g.job_number}
                            </span>
                          )}
                          <span className="text-sm font-medium text-slate-900">{g.job_title || '—'}</span>
                          {g.client_name && <span className="text-xs text-slate-500 ml-2">· {g.client_name}</span>}
                        </div>
                        <div className="text-xs text-slate-500">
                          {format(parseISO(g.date), 'EEE MMM d')} · {g.team_size} on crew
                        </div>
                      </div>
                      <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-wide text-slate-500 bg-white border-b border-slate-100">
                          <tr>
                            <th className="text-left px-3 py-1.5">Worker</th>
                            <th className="text-right px-2 py-1.5">Travel In</th>
                            <th className="text-right px-2 py-1.5">On Job</th>
                            <th className="text-right px-2 py-1.5">Travel Out</th>
                            <th className="text-right px-3 py-1.5">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.members.map((m, mi) => {
                            const isThisUser = m.user_id === varianceModalUser.user_id;
                            return (
                              <tr key={mi} className={`border-b border-slate-100 ${isThisUser ? 'bg-amber-50/40 font-medium' : ''}`}>
                                <td className="px-3 py-1.5 text-slate-700">
                                  <span className="inline-flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.calendar_color || '#94a3b8' }} />
                                    {m.user_name}
                                    {isThisUser && <span className="text-[10px] text-amber-600 ml-1">(this row)</span>}
                                  </span>
                                </td>
                                <td className={`px-2 py-1.5 text-right ${toneFor(m.classes.travel_to_job)}`}>{fmtMin(m.travel_to_job)}</td>
                                <td className={`px-2 py-1.5 text-right ${toneFor(m.classes.time_on_job)}`}>{fmtMin(m.time_on_job)}</td>
                                <td className={`px-2 py-1.5 text-right ${toneFor(m.classes.travel_from_job)}`}>{fmtMin(m.travel_from_job)}</td>
                                <td className="px-3 py-1.5 text-right text-slate-900">{fmtMin(m.total)}</td>
                              </tr>
                            );
                          })}
                          <tr className="bg-slate-100 text-xs text-slate-600">
                            <td className="px-3 py-1.5 italic">Team mean</td>
                            <td className="px-2 py-1.5 text-right">{fmtMin(g.means.travel_to_job)}</td>
                            <td className="px-2 py-1.5 text-right">{fmtMin(g.means.time_on_job)}</td>
                            <td className="px-2 py-1.5 text-right">{fmtMin(g.means.travel_from_job)}</td>
                            <td className="px-3 py-1.5 text-right font-medium">{fmtMin(g.means.total)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            );
          })()}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => varianceModalUser && navigate(`/timesheet?user=${varianceModalUser.user_id}&week=${weekStart}`)}
            >
              Open week
            </Button>
            <Button onClick={() => setVarianceModalUser(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
